// components/footfall/PulseEditor.tsx
'use client';

import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrandMark } from '@/components/ui/BrandMark';
import { Box, Hand, Map as MapIcon, Minus, MousePointer2, Plus, Scan, Store, X, DoorOpen } from 'lucide-react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AURA_GAIN, AURA_RADIUS, AURA_STEP, FLOW_RAMP, FLOW_STOPS, flowAura, levelFeatures, ringCentre, unitName, venueBounds } from '#lib/footfall/pulse';
import { potentialRent } from '#lib/footfall/scenario';
import type { PulseTool } from '#types/types';
import { usePulseWorkspace, derivePulseFromState } from './pulseDerive';
import { MapLayers } from './MapLayers';
import { RecordedPlaces } from './RecordedPlaces';
// Analyst's follow-the-agent chrome (aura pulse, gliding Fater cursor, vanish), reused as Slides reuses it.
import agentFx from '../tables/TableGrid.module.css';
import { agentIsWorking } from '#lib/chat/agentIsWorking';
import { editDraft, loadBuildings, newMove, patchPulse, savePlan, selectShop, setMove, switchScenario } from './pulseState';
import { contextVolumes, levelVolumes } from '#lib/footfall/volumes';
import { ACTIVITY_AURA_INTENSITY, ACTIVITY_GLOW_STOPS, ACTIVITY_RADIUS, activityAura } from '#lib/footfall/activity';

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN ?? '';
const t = (fontSize: number) => ({ fontSize });
const focus = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8AAAEE]';
const island = 'pointer-events-auto rounded-full border border-[#262626] bg-[#1B1B1B] shadow-[0_8px_24px_rgba(0,0,0,0.45)]';
const TOOLS: Array<{ tool: PulseTool; key: string; label: string; icon: typeof Hand }> = [
  { tool: 'select', key: 'v', label: 'Select (V)', icon: MousePointer2 },
  { tool: 'hand', key: 'h', label: 'Pan (H)', icon: Hand },
  { tool: 'relet', key: 'r', label: 'Re-let a shop (R)', icon: Store },
  { tool: 'vacate', key: 'x', label: 'Vacate a shop (X)', icon: DoorOpen },
];
/** Below this zoom shops are too small for a name or a logo: the map shows colour only. */
const LOGO_MIN_ZOOM = 16.2;
const LOGO_MAX = 34;
const LOGO_MIN = 14;
const PADDING = { top: 48, bottom: 80, left: 40, right: 40 };
/** The 3D camera: tilted enough to read heights, not so far that the level in view hides behind its own fronts. */
const PITCH_3D = 55, BEARING_3D = -20;
const LAYERS_2D = ['venue-building', 'venue-amenity', 'venue-unit', 'venue-unit-line', 'venue-moved', 'venue-selected', 'venue-agent', 'venue-agent-fill', 'venue-agent-glow', 'venue-agent-focus', 'venue-label'];
const LAYERS_3D = ['context-3d', 'shops-3d-below', 'shops-3d'];
/** The layer a click or a drop hits shops on, in the view on screen. */
const shopLayer = (k: Kernel | null | undefined) => k?.getState().pulse.view3d ? 'shops-3d' : 'venue-unit';
/** Frames a level: fitted as seen from above, then tilted in 3D, since a tilted fit zooms far out to show the horizon. */
function frame(map: MapboxMap, bounds: [[number, number], [number, number]], view3d: boolean, duration: number) {
  const flat = map.cameraForBounds(bounds, { padding: PADDING, bearing: view3d ? BEARING_3D : 0, pitch: 0, maxZoom: 19.5 });
  // Tilted, the far half of the level recedes: half a zoom step in keeps the level about as large as seen from above.
  map.easeTo({ ...flat, zoom: (flat?.zoom ?? map.getZoom()) + (view3d ? 0.5 : 0), pitch: view3d ? PITCH_3D : 0, bearing: view3d ? BEARING_3D : 0, duration });
}
const EMPTY = { type: 'FeatureCollection' as const, features: [] };
/** The aura's weight at a moment: its heat, swelling and easing by a quarter as a ripple passes along the corridor. */
const auraWeight = (time: number): import('mapbox-gl').ExpressionSpecification => ['*', ['get', 'heat'], ['+', 1, ['*', 0.25, ['sin', ['-', ['get', 'phase'], time]]]]];
/** The measured-activity aura at a fractional hour: each point's share of its shop's peak, blended between the two hours around it, breathing by 6%. */
const hourWeight = (hour: number, time: number): import('mapbox-gl').ExpressionSpecification => {
  const h = Math.floor(hour) % 24, f = hour - Math.floor(hour);
  return ['*', ['+', ['*', ['get', `h${h}`], 1 - f], ['*', ['get', `h${(h + 1) % 24}`], f]], 1 + 0.06 * Math.sin(time * 1.3)];
};
/** A played day runs through the centre's open hours, one hour every SECONDS_PER_HOUR. */
const PLAY_FROM = 7, PLAY_TO = 22, SECONDS_PER_HOUR = 1.4;
/** Mapbox GL's pixels per metre at zoom 0 on the equator (512 px tiles). */
const PX_PER_METRE_Z0 = 512 / 40075016.686;
type MapboxMap = import('mapbox-gl').Map;
type Kernel = NonNullable<ReturnType<typeof useKernel>['kernel']['current']>;

/** A canvas tool button: Studio's tool rail look (near-white circle when chosen), as a real button. */
function ToolButton({ label, selected, onClick, children, testId }: { label: string; selected?: boolean; onClick: () => void; children: React.ReactNode; testId?: string }) {
  return <Tooltip><TooltipTrigger asChild>
    <button type="button" aria-label={label} aria-pressed={selected} data-pulse-tool={testId} onClick={onClick} className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-150 ${selected ? 'bg-[#ECECEC] text-[#0D0D0D]' : 'text-[#B4B4B4] hover:bg-[#2A2A2A] hover:text-white'} ${focus}`}>{children}</button>
  </TooltipTrigger><TooltipContent side="top" sideOffset={8}>{label}</TooltipContent></Tooltip>;
}

/** What a click on a shop does with the active tool. */
function applyTool(k: Kernel, shopId: string | null) {
  const tool = k.getState().pulse.tool;
  if (tool === 'hand') return;
  if (!shopId || tool === 'select') { selectShop(k, shopId); return; }
  const { places, scenario } = derivePulseFromState(k.getState());
  const place = places.find(p => p.feature.id === shopId);
  const existing = scenario.moves.find(m => m.shopId === shopId);
  if (tool === 'vacate') {
    if (place?.category === 'VACANT' && !existing) { selectShop(k, shopId, { notice: 'This shop is already vacant.' }); return; }
    setMove(k, newMove(shopId, { ...existing, type: 'vacate', brandId: null, brandName: null, brandCategory: null, rentPerSqm: null, rentFreeMonths: 0, works: 0 }));
  } else {
    const percentile = place?.feature.exposure?.percentile;
    setMove(k, newMove(shopId, { ...existing, type: 'relet', rentPerSqm: existing?.rentPerSqm ?? (scenario.referenceRent && percentile !== undefined ? potentialRent(scenario.referenceRent, percentile) : null) }));
  }
  selectShop(k, shopId, { notice: null });
}

export function PulseEditor() {
  const { kernel } = useKernel();
  const pulse = useKernelStore(s => s.pulse);
  const w = usePulseWorkspace();
  const { property, venue, places, scenario, levels, level, shortlist } = w;
  const recordedActivity = scenario.layer === 'traffic' && w.trafficView.mode === 'activity';
  const fr = useKernelStore(s => s.language) === 'fr';
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const unsaved = pulse.editVersion !== pulse.savedVersion;

  // ---------- saving ----------
  useEffect(() => {
    if (!unsaved || pulse.saving || pulse.error) return;
    const timer = setTimeout(() => void savePlan(kernel.current), 700);
    return () => clearTimeout(timer);
  }, [pulse.editVersion, pulse.saving, pulse.error, unsaved]);
  useEffect(() => {
    if (!unsaved) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  // ---------- keyboard tools ----------
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.metaKey || event.ctrlKey || event.altKey || target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const k = kernel.current;
      const hit = TOOLS.find(x => x.key === event.key.toLowerCase());
      if (hit) { patchPulse(k, { tool: hit.tool }); return; }
      if (event.key === '3') { patchPulse(k, { view3d: !k?.getState().pulse.view3d }); return; }
      if (event.key === 'Escape') patchPulse(k, { selectedUnitId: null, tool: 'select' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---------- map ----------
  const features = useMemo(() => venue ? levelFeatures(venue, places, level?.id ?? null, scenario.layer, [], w.activityFills) : null, [venue, places, level?.id, scenario.layer, w.activityFills]);
  const bounds = useMemo(() => venue ? venueBounds(venue, level?.id ?? null) : null, [venue, level?.id]);
  const movedIds = useMemo(() => scenario.moves.map(m => m.shopId), [scenario.moves]);
  useEffect(() => {
    if (!MAPBOX_TOKEN || !hostRef.current || mapRef.current) return;
    let disposed = false;
    let observer: ResizeObserver | null = null;
    void (async () => {
      const mapboxgl = (await import('mapbox-gl')).default;
      if (disposed || !hostRef.current) return;
      mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new mapboxgl.Map({ container: hostRef.current, style: 'mapbox://styles/mapbox/dark-v11', center: [2.0438, 48.7829], zoom: 16, attributionControl: false, pitchWithRotate: true, dragRotate: false, touchPitch: false, fadeDuration: 0 });
      mapRef.current = map;
      map.on('load', () => {
        if (disposed) return;
        map.addSource('venue', { type: 'geojson', data: EMPTY });
        map.addLayer({ id: 'venue-building', type: 'fill', source: 'venue', filter: ['==', ['get', 'kind'], 'building'], paint: { 'fill-color': '#242424', 'fill-outline-color': '#333333' } });
        map.addLayer({ id: 'venue-amenity', type: 'fill', source: 'venue', filter: ['==', ['get', 'kind'], 'amenity'], paint: { 'fill-color': '#2E2E2E', 'fill-outline-color': '#3A3A3A' } });
        map.addLayer({ id: 'venue-unit', type: 'fill', source: 'venue', filter: ['==', ['get', 'kind'], 'unit'], paint: { 'fill-color': ['get', 'colour'], 'fill-opacity': .88 } });
        map.addLayer({ id: 'venue-unit-line', type: 'line', source: 'venue', filter: ['==', ['get', 'kind'], 'unit'], paint: { 'line-color': '#000000', 'line-width': .8 } });
        map.addSource('venue-flow', { type: 'geojson', data: EMPTY });
        // Traffic as an aura: a heat glow along the corridors, hotter where more of the walks pass.
        map.addLayer({ id: 'venue-flow', type: 'heatmap', source: 'venue-flow', layout: { visibility: 'none' }, paint: {
          'heatmap-weight': auraWeight(0),
          'heatmap-intensity': 3 * AURA_STEP / AURA_RADIUS * AURA_GAIN,
          'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgba(0,0,0,0)', ...FLOW_RAMP.flatMap((c, i) => [FLOW_STOPS[i]!, c])],
          'heatmap-opacity': 0.85 } });
        map.addSource('venue-activity', { type: 'geojson', data: EMPTY });
        // Measured activity: each shop's outline glows and spills into its corridor, hotter the nearer the hour is to its usual peak.
        map.addLayer({ id: 'venue-activity', type: 'heatmap', source: 'venue-activity', layout: { visibility: 'none' }, paint: {
          'heatmap-weight': hourWeight(17, 0),
          'heatmap-intensity': ACTIVITY_AURA_INTENSITY,
          'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgba(0,0,0,0)', ...FLOW_RAMP.flatMap((c, i) => [ACTIVITY_GLOW_STOPS[i]!, c])],
          'heatmap-opacity': 0.9 } });
        map.addLayer({ id: 'venue-moved', type: 'line', source: 'venue', filter: ['in', ['get', 'id'], ['literal', []]], paint: { 'line-color': '#F0F0F0', 'line-width': 2, 'line-dasharray': [2, 1.5] } });
        map.addLayer({ id: 'venue-selected', type: 'line', source: 'venue', filter: ['==', ['get', 'id'], ''], paint: { 'line-color': '#FFFFFF', 'line-width': 2.5 } });
        map.addLayer({ id: 'venue-agent', type: 'line', source: 'venue', filter: ['in', ['get', 'id'], ['literal', []]], paint: { 'line-color': '#3B82F6', 'line-width': 3, 'line-blur': 2, 'line-opacity': 0 } });
        map.addLayer({ id: 'venue-agent-fill', type: 'fill', source: 'venue', filter: ['==', ['get', 'id'], ''], paint: { 'fill-color': '#3B82F6', 'fill-opacity': 0 } });
        map.addLayer({ id: 'venue-agent-glow', type: 'line', source: 'venue', filter: ['==', ['get', 'id'], ''], paint: { 'line-color': '#3B82F6', 'line-width': 10, 'line-blur': 8, 'line-opacity': 0.55 } });
        map.addLayer({ id: 'venue-agent-focus', type: 'line', source: 'venue', filter: ['==', ['get', 'id'], ''], paint: { 'line-color': '#5B9BFF', 'line-width': 2.5 } });
        map.addSource('context-3d', { type: 'geojson', data: EMPTY });
        map.addSource('shops-3d', { type: 'geojson', data: EMPTY });
        const hidden = { visibility: 'none' as const };
        map.addLayer({ id: 'context-3d', type: 'fill-extrusion', source: 'context-3d', layout: hidden, paint: { 'fill-extrusion-color': '#2A2A2A', 'fill-extrusion-height': ['get', 'height'], 'fill-extrusion-opacity': 0.75 } });
        map.addLayer({ id: 'shops-3d-below', type: 'fill-extrusion', source: 'shops-3d', layout: hidden, filter: ['==', ['get', 'role'], 'below'], paint: { 'fill-extrusion-color': '#3A3A3A', 'fill-extrusion-base': ['get', 'base'], 'fill-extrusion-height': ['get', 'top'], 'fill-extrusion-opacity': 0.55 } });
        map.addLayer({ id: 'shops-3d', type: 'fill-extrusion', source: 'shops-3d', layout: hidden, filter: ['==', ['get', 'role'], 'active'], paint: { 'fill-extrusion-color': ['get', 'colour'], 'fill-extrusion-base': ['get', 'base'], 'fill-extrusion-height': ['get', 'top'], 'fill-extrusion-opacity': 0.95 } });
        map.addLayer({ id: 'venue-label', type: 'symbol', source: 'venue', minzoom: LOGO_MIN_ZOOM, filter: ['all', ['==', ['get', 'kind'], 'unit'], ['==', ['get', 'logo'], 0]], layout: { 'text-field': ['case', ['==', ['get', 'label'], ''], ['format', ['get', 'unit'], { 'text-color': '#AFAFAF', 'font-scale': 0.85 }], ['format', ['get', 'label'], {}, '\n', {}, ['get', 'unit'], { 'text-color': '#AFAFAF', 'font-scale': 0.85 }]], 'text-size': 11, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'], 'text-max-width': 7 }, paint: { 'text-color': '#ECECEC', 'text-halo-color': '#000000', 'text-halo-width': 1.2 } });
        setMapReady(true);
      });
      map.on('click', event => {
        const k = kernel.current;
        const observations = ['recorded-places', 'recorded-place-selected'].filter(id => map.getLayer(id));
        if (observations.length && map.queryRenderedFeatures(event.point, { layers: observations }).length) return;
        const layer = shopLayer(k);
        const hit = map.getLayer(layer) ? map.queryRenderedFeatures(event.point, { layers: [layer] })[0] : undefined;
        if (k) applyTool(k, (hit?.properties?.id as string | undefined) ?? null);
      });
      map.on('mousemove', event => {
        const tool = kernel.current?.getState().pulse.tool;
        const layer = shopLayer(kernel.current);
        const overShop = !!map.getLayer(layer) && map.queryRenderedFeatures(event.point, { layers: [layer] }).length > 0;
        map.getCanvas().style.cursor = tool === 'hand' ? 'grab' : overShop ? (tool === 'select' ? 'pointer' : 'copy') : '';
      });
      observer = new ResizeObserver(() => map.resize());
      observer.observe(hostRef.current);
    })();
    return () => { disposed = true; observer?.disconnect(); mapRef.current?.remove(); mapRef.current = null; };
  }, []);
  useEffect(() => {
    const source = mapRef.current?.getSource('venue') as import('mapbox-gl').GeoJSONSource | undefined;
    if (mapReady && source) source.setData(features ?? EMPTY);
  }, [mapReady, features]);
  // Fit when the level changes; a later layer change keeps the zoom.
  const fitted = useRef<typeof bounds>(null);
  useEffect(() => {
    if (!mapReady || !bounds || fitted.current === bounds) return;
    fitted.current = bounds;
    if (mapRef.current) frame(mapRef.current, bounds, !!kernel.current?.getState().pulse.view3d, 0);
  }, [mapReady, bounds]);
  // The agent's zoom, declared after the level fit so a level switch in the same command is framed by the zoom.
  useEffect(() => {
    const zoomTo = pulse.zoom && venue ? venueBounds(venue, level?.id ?? null, pulse.zoom.shopIds) : null;
    if (mapReady && mapRef.current && zoomTo) frame(mapRef.current, zoomTo, pulse.view3d, 800);
  }, [mapReady, pulse.zoom?.at]);
  useEffect(() => {
    if (mapReady) mapRef.current?.setFilter('venue-moved', ['in', ['get', 'id'], ['literal', movedIds]]);
  }, [mapReady, movedIds]);
  // ---------- traffic aura ----------
  const aura = useMemo(() => {
    if (!venue?.flows?.length || !level) return [];
    const maxWalks = Math.max(...venue.flows.map(f => f.walks));
    return flowAura(venue.flows.filter(f => f.level === level.id), venue.features.filter(f => f.kind === 'unit' && f.level === level.id).flatMap(f => f.rings), maxWalks);
  }, [venue, level]);
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    (map.getSource('venue-flow') as import('mapbox-gl').GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features: aura });
    const lat = aura[0]?.geometry.coordinates[1];
    // AURA_RADIUS metres at every zoom: exponential base 2 between two stops is exactly proportional to the map scale.
    if (lat !== undefined) { const r = AURA_RADIUS * PX_PER_METRE_Z0 / Math.cos(lat * Math.PI / 180); map.setPaintProperty('venue-flow', 'heatmap-radius', ['interpolate', ['exponential', 2], ['zoom'], 15, r * 2 ** 15, 20, r * 2 ** 20]); }
    const shown = scenario.layer === 'traffic' && w.trafficView.mode === 'routes' && aura.length > 0;
    map.setLayoutProperty('venue-flow', 'visibility', shown ? 'visible' : 'none');
    if (!shown || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // A slow ripple, re-weighted about thirty times a second: smooth enough to read as breathing, cheap for a heat map.
    const timer = window.setInterval(() => { if (map.getLayer('venue-flow')) map.setPaintProperty('venue-flow', 'heatmap-weight', auraWeight(performance.now() / 1000 * 0.7)); }, 33);
    return () => window.clearInterval(timer);
  }, [mapReady, aura, scenario.layer, w.trafficView.mode]);

  // ---------- measured activity aura ----------
  const activityPoints = useMemo(() => recordedActivity && level ? activityAura(places.filter(p => p.feature.level === level.id), w.activityCurves) : [], [recordedActivity, places, level, w.activityCurves]);
  const { hour: viewHour, playing } = w.trafficView;
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    (map.getSource('venue-activity') as import('mapbox-gl').GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features: activityPoints });
    // ACTIVITY_RADIUS metres at every zoom (exponential base 2 between two stops is exactly proportional).
    const lat = (activityPoints[0]?.geometry.coordinates[1]) ?? 48.8, r = ACTIVITY_RADIUS * PX_PER_METRE_Z0 / Math.cos(lat * Math.PI / 180);
    map.setPaintProperty('venue-activity', 'heatmap-radius', ['interpolate', ['exponential', 2], ['zoom'], 15, r * 2 ** 15, 20, r * 2 ** 20]);
    map.setLayoutProperty('venue-activity', 'visibility', recordedActivity && !pulse.view3d && activityPoints.length ? 'visible' : 'none');
  }, [mapReady, activityPoints, recordedActivity, pulse.view3d]);
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !recordedActivity) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const paint = (hour: number, time: number) => { if (map.getLayer('venue-activity')) map.setPaintProperty('venue-activity', 'heatmap-weight', hourWeight(hour, still ? 0 : time)); };
    paint(viewHour, 0);
    if (still && !playing) return;
    // Playing, the day advances smoothly from the shared hour, which follows it (each new hour restarts this from there); paused, the aura only breathes.
    const start = performance.now(), from = viewHour < PLAY_FROM || viewHour >= PLAY_TO ? PLAY_FROM : viewHour;
    const timer = window.setInterval(() => {
      const seconds = (performance.now() - start) / 1000;
      if (!playing) { paint(viewHour, seconds); return; }
      const hour = PLAY_FROM + ((from - PLAY_FROM + seconds / SECONDS_PER_HOUR) % (PLAY_TO - PLAY_FROM));
      paint(hour, seconds);
      const k = kernel.current, view = k?.getState().pulse.trafficView;
      if (k && view && Math.floor(hour) !== view.hour) patchPulse(k, { trafficView: { ...view, hour: Math.floor(hour) } });
    }, 33);
    return () => window.clearInterval(timer);
  }, [mapReady, recordedActivity, playing, viewHour]);

  // ---------- 3D ----------
  const { volumes } = w;
  const view3d = pulse.view3d;
  const shops3d = useMemo(() => view3d && volumes ? levelVolumes(places, volumes, w.levels, level?.id ?? null, scenario.layer, w.activityColours) : EMPTY, [view3d, volumes, places, w.levels, level?.id, scenario.layer, w.activityColours]);
  const context3d = useMemo(() => view3d && volumes ? contextVolumes(volumes) : EMPTY, [view3d, volumes]);
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    (map.getSource('shops-3d') as import('mapbox-gl').GeoJSONSource | undefined)?.setData(shops3d);
    (map.getSource('context-3d') as import('mapbox-gl').GeoJSONSource | undefined)?.setData(context3d);
  }, [mapReady, shops3d, context3d]);
  useEffect(() => {
    const map = mapRef.current;
    const k = kernel.current;
    if (!mapReady || !map) return;
    if (view3d && k && property) void loadBuildings(k, property.id);
    for (const id of LAYERS_2D) map.setLayoutProperty(id, 'visibility', view3d ? 'none' : 'visible');
    for (const id of LAYERS_3D) map.setLayoutProperty(id, 'visibility', view3d ? 'visible' : 'none');
    if (view3d) { map.dragRotate.enable(); map.touchPitch.enable(); } else { map.dragRotate.disable(); map.touchPitch.disable(); }
    if (bounds) frame(map, bounds, view3d, 700);
  }, [mapReady, view3d]);

  // A shop selected from the panel or by the agent on another level: switch level, then centre on it.
  // Each linked shop shows its brand's logo tile, sized to the shop, on the level in view. Rebuilt only when the logos
  // on that level change, not on every selection.
  // The plan shows the centre as it will be: a re-let shop carries its incoming brand, a vacated one none.
  const logos = useMemo(() => places.filter(p => p.feature.level === level?.id).flatMap(p => {
    const move = scenario.moves.find(m => m.shopId === p.feature.id);
    const incoming = move?.type === 'relet' && move.brandId ? shortlist.find(b => b.id === move.brandId) : undefined;
    const logoUrl = move ? (incoming?.logoUrl ?? null) : p.logoUrl;
    const name = move ? (incoming?.name ?? move.brandName) : p.name;
    const altitude = view3d ? (volumes?.shops[p.feature.id]?.top ?? 0) + 0.3 : 0;
    return logoUrl ? [{ id: p.feature.id, unit: unitName(p.feature), logoUrl, name, altitude, sideMetres: Math.sqrt(p.feature.areaSqm), at: ringCentre([p.feature.rings.reduce((a, b) => b.length > a.length ? b : a)]) }] : [];
  }), [places, level?.id, scenario.moves, shortlist, view3d, volumes]);
  const logoKey = logos.map(l => `${l.id}:${l.logoUrl}:${l.altitude}`).join('|');
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    let cancelled = false;
    const made: Array<{ marker: import('mapbox-gl').Popup; root: ReturnType<typeof createRoot>; tile: HTMLElement; caption: HTMLElement; sideMetres: number; at: [number, number] }> = [];
    // A logo takes about two thirds of its shop's width on screen, up to LOGO_MAX px; a shop too small to hold a
    // readable one shows its trade colour only. Measured per shop, so in 3D the far side of the centre recedes like
    // its shops do instead of piling up full-size logos.
    const show = () => {
      for (const m of made) {
        const [lng, lat] = m.at;
        const a = map.project(m.at), b = map.project([lng + m.sideMetres / (111320 * Math.cos(lat * Math.PI / 180)), lat]);
        const size = Math.min(LOGO_MAX, Math.hypot(b.x - a.x, b.y - a.y) * 0.66);
        m.tile.style.visibility = m.caption.style.visibility = size < LOGO_MIN ? 'hidden' : '';
        m.tile.style.transform = `scale(${size / LOGO_MAX})`;
      }
    };
    void import('mapbox-gl').then(({ default: mapboxgl }) => {
      if (cancelled) return;
      for (const logo of logos) {
        const el = document.createElement('div');
        el.style.pointerEvents = 'none';
        el.style.display = 'flex'; el.style.flexDirection = 'column'; el.style.alignItems = 'center';
        const tile = el.appendChild(document.createElement('div'));
        // The tile shrinks towards its foot, so the number below it stays tucked under the logo at any size.
        tile.style.transformOrigin = 'bottom center';
        const caption = el.appendChild(document.createElement('div'));
        caption.textContent = logo.unit;
        Object.assign(caption.style, { marginTop: '2px', padding: '0 4px', borderRadius: '4px', background: 'rgba(27,27,27,0.85)', color: '#ECECEC', fontSize: '10px', lineHeight: '14px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' });
        const root = createRoot(tile);
        root.render(<BrandMark logoUrl={logo.logoUrl} name={logo.name} size={LOGO_MAX} radius={8} raised />);
        // A popup rather than a marker: only a popup can stand at an altitude, which puts the logo on its shop's roof in 3D.
        const marker = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, closeOnMove: false, focusAfterOpen: false, anchor: 'center', className: 'pulse-logo', maxWidth: 'none' })
          .setLngLat(logo.at).setDOMContent(el).setAltitude(logo.altitude).addTo(map);
        made.push({ marker, root, tile, caption, sideMetres: logo.sideMetres, at: logo.at });
      }
      show();
    });
    map.on('move', show);
    return () => {
      cancelled = true;
      map.off('move', show);
      for (const m of made) { m.marker.remove(); queueMicrotask(() => m.root.unmount()); }
    };
  }, [mapReady, logoKey]);
  const selected = places.find(p => p.feature.id === pulse.selectedUnitId) ?? null;
  useEffect(() => {
    if (selected?.feature.level && selected.feature.level !== level?.id) editDraft(kernel.current, d => ({ ...d, levelId: selected.feature.level }));
  }, [pulse.selectedUnitId]);
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    map.setFilter('venue-selected', ['==', ['get', 'id'], pulse.selectedUnitId ?? '']);
    const centre = selected ? ringCentre(selected.feature.rings) : null;
    if (centre && !map.getBounds()?.contains(centre)) map.easeTo({ center: centre, zoom: Math.max(map.getZoom(), 17.5), duration: 400 });
  }, [mapReady, pulse.selectedUnitId, level?.id]);

  // In recorded 3D, mark selection above the roof without replacing the shop's measured colour.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !recordedActivity || !view3d || !selected) return;
    let cancelled = false;
    let marker: import('mapbox-gl').Popup | undefined;
    void import('mapbox-gl').then(({ default: mapboxgl }) => {
      if (cancelled) return;
      const el = document.createElement('div');
      el.dataset.testid = 'traffic-selected-marker';
      Object.assign(el.style, { width: '42px', height: '42px', border: '2px solid white', borderRadius: '50%', boxShadow: '0 0 0 1px #1B1B1B', pointerEvents: 'none' });
      marker = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, closeOnMove: false, focusAfterOpen: false, anchor: 'center', className: 'pulse-logo', maxWidth: 'none' })
        .setLngLat(ringCentre(selected.feature.rings)).setDOMContent(el).setAltitude((volumes?.shops[selected.feature.id]?.top ?? 0) + 0.6).addTo(map);
    });
    return () => { cancelled = true; marker?.remove(); };
  }, [mapReady, recordedActivity, view3d, selected, volumes]);

  // ---------- the agent at work ----------
  // The aura lights while the plan's agent works and dissolves when it stops; its cursor sits on the shop it last
  // changed and glides to the next; the shops it touched this run pulse on the map. Cleared once the run has settled.
  const agentBusy = useKernelStore(s => {
    const id = s.activeChatSessionId;
    const session = id ? s.inventory.ChatSession.registry[id] : null;
    return session?.creationModule === 'pulse' && agentIsWorking(session.runtime?.chatState, session.runtime?.runningScriptId);
  });
  const [auraPhase, setAuraPhase] = useState<'off' | 'on' | 'vanishing'>('off');
  // A phase machine driven by an external store, not derived state: it has to outlive the busy flag by its fade.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (agentBusy) { setAuraPhase('on'); return; }
    if (auraPhase !== 'on') return;
    const settle = setTimeout(() => setAuraPhase('vanishing'), 1500);
    return () => clearTimeout(settle);
  }, [agentBusy, auraPhase]);
  useEffect(() => {
    if (auraPhase !== 'vanishing') return;
    const off = setTimeout(() => { setAuraPhase('off'); patchPulse(kernel.current, { agentEdit: null, agentTouched: [] }); }, 640);
    return () => clearTimeout(off);
  }, [auraPhase]);
  // The agent's focus is shown on the shop's own shape: its outline in 2D, the box itself in 3D. Only while it works.
  const agentFocusId = auraPhase !== 'off' ? pulse.agentEdit?.shopId ?? '' : '';
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map?.getLayer('shops-3d')) return;
    map.setPaintProperty('shops-3d', 'fill-extrusion-color', recordedActivity ? ['get', 'colour'] : ['case', ['==', ['get', 'id'], agentFocusId], '#3B82F6', ['==', ['get', 'id'], pulse.selectedUnitId ?? ''], '#F0F0F0', ['get', 'colour']]);
    for (const id of ['venue-agent-fill', 'venue-agent-glow', 'venue-agent-focus']) map.setFilter(id, ['==', ['get', 'id'], agentFocusId]);
  }, [mapReady, pulse.selectedUnitId, agentFocusId, recordedActivity]);
  // The shop being worked on pulses: its blue wash and glow breathe while the agent works, and settle as the aura fades.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map?.getLayer('venue-agent-fill') || !agentFocusId) return;
    let frame = 0;
    const tick = (now: number) => {
      const beat = auraPhase === 'on' ? (1 + Math.sin(now / 260)) / 2 : 0;
      map.setPaintProperty('venue-agent-fill', 'fill-opacity', recordedActivity ? 0 : 0.12 + 0.33 * beat);
      map.setPaintProperty('venue-agent-glow', 'line-opacity', 0.35 + 0.5 * beat);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(frame); if (map.getLayer('venue-agent-fill')) map.setPaintProperty('venue-agent-fill', 'fill-opacity', 0); };
  }, [mapReady, agentFocusId, auraPhase, recordedActivity]);
  const cursorRef = useRef<HTMLDivElement>(null);
  const agentShop = places.find(p => p.feature.id === pulse.agentEdit?.shopId) ?? null;
  useEffect(() => {
    const map = mapRef.current, el = cursorRef.current;
    if (!mapReady || !map || !el || !agentShop) return;
    // Projected on every camera move, so the cursor stays on its shop while the map pans and tilts.
    const place = () => {
      const tip = map.project(ringCentre([agentShop.feature.rings.reduce((a, b) => b.length > a.length ? b : a)]));
      // The pointer's tip sits 16 x 11 px into its box (see .agentCursorPointer).
      el.style.transform = `translate(${tip.x - 16}px, ${tip.y - 11}px)`;
    };
    place();
    map.on('move', place);
    return () => { map.off('move', place); };
  }, [mapReady, agentShop, auraPhase === 'off']);
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map?.getLayer('venue-agent')) return;
    map.setFilter('venue-agent', ['in', ['get', 'id'], ['literal', pulse.agentTouched]]);
    if (auraPhase === 'off' || !pulse.agentTouched.length) { map.setPaintProperty('venue-agent', 'line-opacity', 0); return; }
    // The touched shops breathe with the aura: a slow pulse of their outline, settled to steady while the aura fades.
    let frame = 0;
    const tick = (now: number) => {
      map.setPaintProperty('venue-agent', 'line-opacity', auraPhase === 'on' ? 0.55 + 0.45 * Math.sin(now / 380) : 0.4);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [mapReady, pulse.agentTouched, auraPhase]);

  if (!MAPBOX_TOKEN) return <p className="p-8 text-[#8E8E8E]" style={t(14)}>The map is not configured on this environment.</p>;
  const status = !property ? 'Loading the centre' : property.venue === undefined ? 'Loading the plan' : !venue ? 'This centre has no indoor map yet.' : null;
  const k = kernel.current;

  return <TooltipProvider><div className="relative h-full w-full overflow-hidden bg-black text-[#ECECEC]" data-testid="pulse-editor"
>
    {/* mapbox-gl.css sets the host to position: relative, so the host fills a positioned wrapper instead of being absolute itself. */}
    <div className="absolute inset-0 [&_.mapboxgl-ctrl-logo]:!hidden [&_.pulse-logo]:!pointer-events-none [&_.pulse-logo_*]:!pointer-events-none [&_.pulse-logo_.mapboxgl-popup-content]:!bg-transparent [&_.pulse-logo_.mapboxgl-popup-content]:!p-0 [&_.pulse-logo_.mapboxgl-popup-content]:!shadow-none [&_.pulse-logo_.mapboxgl-popup-tip]:!hidden"><div ref={hostRef} className="h-full w-full" role="region" aria-label={property ? `Plan of ${property.name}` : 'Plan'} /></div>
    {status && <p className="absolute inset-0 flex items-center justify-center bg-black text-[#8E8E8E]" role="status" style={t(14)}>{status}</p>}
    <div aria-hidden className={`${agentFx.agentGlow} ${auraPhase === 'on' ? agentFx.agentGlowOn : auraPhase === 'vanishing' ? agentFx.agentGlowVanish : ''}`} />
    {agentShop && auraPhase !== 'off' && <div ref={cursorRef} aria-hidden data-testid="agent-cursor" className={`${agentFx.agentCursor} ${auraPhase === 'vanishing' ? agentFx.agentCursorVanish : ''}`} style={{ opacity: 1, zIndex: 15 }}>
      <svg className={agentFx.agentCursorPointer} width="20" height="24" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 2.5 L5 21.5 L9.7 17 L12.6 23.4 L15.3 22.1 L12.5 15.9 L18.6 15.9 Z" fill="#FFFFFF" stroke="#1F2937" strokeWidth="1" strokeLinejoin="round" /></svg>
    </div>}

    {pulse.notice && <div role="status" className="absolute left-1/2 top-16 z-10 flex max-w-[80%] -translate-x-1/2 items-center gap-3 rounded-full border border-white/[0.06] bg-[#353535] py-1.5 pl-4 pr-1.5 text-[#ECECEC] shadow-[0_8px_24px_rgba(0,0,0,0.45)]" style={t(13)}>{pulse.notice}<button type="button" className={`rounded-full p-1 text-[#B4B4B4] hover:bg-[#404040] ${focus}`} aria-label="Dismiss" onClick={() => patchPulse(k, { notice: null })}><X size={14} /></button></div>}

    {/* The scenario on the map, as page tabs: shown once the plan holds more than one. */}
    {w.scenarios.length > 1 && !status && <div role="tablist" aria-label="Scenario" className={`${island} absolute left-1/2 top-3 z-10 flex max-w-[calc(100%-120px)] -translate-x-1/2 items-center gap-0.5 p-1`}>
      {w.scenarios.map(s => <button key={s.id} type="button" role="tab" aria-selected={s.active} onClick={() => switchScenario(k, s.id)}
        className={`h-8 min-w-0 max-w-[12rem] overflow-hidden text-ellipsis whitespace-nowrap rounded-full px-3 transition-colors ${s.active ? 'bg-white/[0.12] text-[#ECECEC]' : 'text-[#8E8E8E] hover:text-[#ECECEC]'} ${focus}`} style={t(13)}>{s.name}</button>)}
    </div>}

    {/* Floors, picked on the map itself as indoor maps do: highest at the top. */}
    {levels.length > 1 && !status && <div role="group" aria-label="Level" className={`${island} absolute right-3 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-0.5 p-1`}>
      {levels.slice().reverse().map(l => <ToolButton key={l.id} testId={`level-${l.name}`} label={`Level ${l.name}`} selected={level?.id === l.id} onClick={() => { editDraft(k, d => ({ ...d, levelId: l.id })); patchPulse(k, { selectedUnitId: null }); }}><span className="font-medium tabular-nums" style={t(13)}>{l.name}</span></ToolButton>)}
    </div>}
    {!status && <MapLayers w={w} view3d={view3d} fr={fr} />}
    {!status && <RecordedPlaces mapRef={mapRef} ready={mapReady} workspace={w} />}

    <div role="toolbar" aria-label="Plan tools" className={`${island} absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-0.5 p-1`}>
      {TOOLS.map(({ tool, label, icon: Icon }) => <ToolButton key={tool} testId={tool} label={label} selected={pulse.tool === tool} onClick={() => patchPulse(k, { tool })}><Icon size={18} /></ToolButton>)}
      <span className="mx-1 h-5 w-px bg-[#2E2E2E]" aria-hidden />
      <ToolButton label="Zoom out" onClick={() => mapRef.current?.zoomOut({ duration: 160 })}><Minus size={18} /></ToolButton>
      <ToolButton label="Zoom in" onClick={() => mapRef.current?.zoomIn({ duration: 160 })}><Plus size={18} /></ToolButton>
      <ToolButton label="Fit the level" onClick={() => { if (bounds && mapRef.current) frame(mapRef.current, bounds, view3d, 300); }}><Scan size={17} /></ToolButton>
      <span className="mx-1 h-5 w-px bg-[#2E2E2E]" aria-hidden />
      <ToolButton testId="3d" label={view3d ? 'Back to the plan view (3)' : '3D view (3)'} selected={view3d} onClick={() => patchPulse(k, { view3d: !view3d })}>{view3d ? <MapIcon size={18} /> : <Box size={18} />}</ToolButton>
    </div>
  </div></TooltipProvider>;
}
